/**
 * 데모 모드 세션 관리
 *
 * 데모 모드는 "실제 서버에 붙지 않는 체험판"이다.
 * - 로그인 화면의 [데모 버전 체험하기] 버튼이 startDemoSession() 을 호출한다.
 * - 이후 모든 ERP API 호출은 demoApi.js 의 fetch 인터셉터가 가로채서
 *   demoData.js 의 샘플 데이터를 돌려준다. 실제 서버로는 아무것도 나가지 않는다.
 * - 세션 정보는 sessionStorage 에만 저장하므로 탭을 닫으면 흔적 없이 사라진다.
 *
 * 인증 우회 방식:
 *   authService / tokenManager 를 고치지 않기 위해, 실제 로그인과 똑같은 저장소 키에
 *   "서명 없는 가짜 JWT" 를 넣어 둔다. tokenManager.parseJwt 는 payload 를 base64 디코딩만
 *   하므로 이 토큰으로도 companyNo / userNo 가 정상적으로 읽힌다.
 */

export const DEMO_FLAG_KEY = 'trp_demo_mode';
export const DEMO_TOUR_KEY = 'trp_demo_tour_seen';

/** 데모 회사/사용자 식별자. demoData.js 와 값이 같아야 한다. */
export const DEMO_COMPANY_NO = '99001';
export const DEMO_USER_NO = '990001';
export const DEMO_USER_EMAIL = 'demo@tripbox.co.kr';

/** 실제 로그인과 동일한 저장소 키 (authService.STORAGE_KEYS 와 일치) */
const AUTH_KEYS = {
  token: 'authToken',
  refreshToken: 'authRefreshToken',
  expirationTime: 'authExpirationTime',
  custNo: 'authCustNo',
  userName: 'authUserName',
};

function base64UrlEncode(obj) {
  const json = JSON.stringify(obj);
  // 한글이 포함될 수 있으므로 UTF-8 → latin1 변환 후 btoa
  const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) =>
    String.fromCharCode(parseInt(p1, 16))
  );
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 서명이 없는(=서버에 보내지 않는) 데모 전용 토큰 생성 */
function buildDemoToken() {
  const header = base64UrlEncode({ alg: 'none', typ: 'JWT' });
  const payload = base64UrlEncode({
    unique_name: DEMO_USER_EMAIL,
    email: DEMO_USER_EMAIL,
    CompanyNo: DEMO_COMPANY_NO,
    UserNo: DEMO_USER_NO,
    MemberNo: DEMO_USER_NO,
    demo: true,
  });
  return `${header}.${payload}.demo`;
}

/** 현재 데모 모드인지 */
export function isDemoMode() {
  try {
    return window.sessionStorage.getItem(DEMO_FLAG_KEY) === '1';
  } catch (_) {
    return false;
  }
}

/**
 * 데모 세션 시작.
 * 실제 로그인 상태와 동일한 형태로 sessionStorage 를 채우므로
 * RequireAuth / tokenManager 를 손대지 않아도 그대로 통과한다.
 */
export function startDemoSession() {
  const expiration = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
  const store = window.sessionStorage;

  // 혹시 남아 있는 실제 로그인 정보와 섞이지 않도록 먼저 비운다.
  Object.values(AUTH_KEYS).forEach((k) => {
    try {
      window.localStorage.removeItem(k);
    } catch (_) {}
    try {
      store.removeItem(k);
    } catch (_) {}
  });

  store.setItem(AUTH_KEYS.token, buildDemoToken());
  store.setItem(AUTH_KEYS.refreshToken, 'demo-refresh');
  store.setItem(AUTH_KEYS.expirationTime, expiration);
  store.setItem(AUTH_KEYS.custNo, DEMO_COMPANY_NO);
  store.setItem(AUTH_KEYS.userName, DEMO_USER_EMAIL);
  store.setItem(DEMO_FLAG_KEY, '1');
  store.removeItem(DEMO_TOUR_KEY);
}

/** 데모 세션 종료 (로그아웃과 함께 호출) */
export function endDemoSession() {
  try {
    const store = window.sessionStorage;
    Object.values(AUTH_KEYS).forEach((k) => store.removeItem(k));
    store.removeItem(DEMO_FLAG_KEY);
    store.removeItem(DEMO_TOUR_KEY);
  } catch (_) {}
}

/** 이 화면의 튜토리얼을 이미 봤는지 (라우트 단위로 기록) */
export function isTourSeen(routeKey) {
  try {
    const raw = window.sessionStorage.getItem(DEMO_TOUR_KEY);
    if (!raw) return false;
    const seen = JSON.parse(raw);
    return Array.isArray(seen) && seen.includes(routeKey);
  } catch (_) {
    return false;
  }
}

export function markTourSeen(routeKey) {
  try {
    const raw = window.sessionStorage.getItem(DEMO_TOUR_KEY);
    const seen = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(seen) ? seen : [];
    if (!next.includes(routeKey)) next.push(routeKey);
    window.sessionStorage.setItem(DEMO_TOUR_KEY, JSON.stringify(next));
  } catch (_) {}
}

/** 튜토리얼 기록 초기화 (다시 보기) */
export function resetTourSeen() {
  try {
    window.sessionStorage.removeItem(DEMO_TOUR_KEY);
  } catch (_) {}
}
